import React, { useState, useEffect, useRef } from 'react';
import { UploadOutlined } from '@ant-design/icons';

// make sure you include the timeline stylesheet or the timeline will not be styled
import 'react-calendar-timeline/lib/Timeline.css';
import moment from 'moment';
import 'src/app/popup.scss';

import styles from 'src/app/pages/ObjectiveManagement/tabs/ObjectiveTab/Objective.module.scss';
import { DraggableModal } from 'src/app/components';
import 'gantt-task-react/dist/index.css';
import { useDispatch, useSelector } from 'react-redux';
import { reduxRootTypes } from 'src/types';
import { contentsActions } from 'src/app/redux/actions';
import { errorLocals } from 'src/locals';
import { logsService, objectiveService } from 'src/app/services';
import { attachmentHelper, statusHelper } from 'src/app/helpers';
import { TrainingObjectiveDataType } from 'src/types/services/objective-service.type';
import { OntologyDataType } from 'src/types/services/ontology-service.type';
import { OntologyEnum } from 'src/enums/ontology.enum';
import OntologyForm from '../OntologiesForm/ontologiesForm';
import FileTable from '../../FileTable/fileTable';
import { FileTableType, TrainingObjectiveFormType } from './objectiveForm.config';

import { flowOMMEnums } from 'src/enums/flow.enum';
import { displaySuccessStatusMessage } from 'src/app/helpers/status.helper';
import { transactionForSelectList } from 'src/app/helpers/statusAndTransaction.helper';
import { checkPermissionList } from 'src/app/helpers/permissions.helper';
import { PermissionEnum } from 'src/enums/permission.enum';
import TextArea from 'antd/es/input/TextArea';
import Logs from '../../Logs/Logs';
import { LogsData } from '../../Logs/logs.config';

import { useTranslation } from 'react-i18next';
import { predefinedOntologies } from 'src/app/pages/Administration/tabs/OntologyTab/ontology.config';
import { defaultTrainingObjectiveData } from 'src/app/pages/ObjectiveManagement/tabs/ObjectiveTab/Objective.config';
import { useUsers } from 'src/app/context/UserContext';

import Input from 'antd/es/input';
import Form from 'antd/es/form';
import Button from 'antd/es/button';
import Select from 'antd/es/select';
import DatePicker from 'antd/es/date-picker';
import Popover from 'antd/es/popover';
import i18n from 'src/i18n';

const TextAreaWithPopover: React.FC<any> = ({ content, title, name, value, onFormChange }) => {
  const { t } = useTranslation();

  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  return (
    <Popover content={content} visible={isHovered && !isFocused && value}>
      <div className="popup-input-container-private">
        <span className="popup-input-title">{title}</span>
        <Form.Item name={name} rules={[{ required: true, message: t('general.required') || '' }]}>
          <TextArea
            className={styles.textarea_input}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            autoSize={{ minRows: 1, maxRows: 3 }}
            value={value}
            onChange={(e) => onFormChange(name, e.target.value)}
          />
        </Form.Item>
      </div>
    </Popover>
  );
};

const TrainingObjectiveForm: React.FC<TrainingObjectiveFormType> = ({
  setOnClickOpen,
  onClickOpen,
  trainingObjectiveForm,
  isEditMode,
}) => {
  const { t } = useTranslation();

  const { eventName } = useSelector((state: reduxRootTypes.RootStateType) => state.eventState);

  const defaultTrainingObjectiveDatas = {
    id: '',
    eventId: eventName,
    trainingObjectiveNumber: '',
    trainingObjective: '',
    creationDate: undefined,
    modificationDate: undefined,
    relatedStandards: '',
    relatedAudienceEntities: [],
    relatedAudienceProcesses: [],
    scenarioConditions: '',
    scenarioConditionStatus: '',
    milConditions: '',
    milConditionStatus: '',
    exerciseControlConditions: '',
    exerciseControlConditionStatus: '',
    trainingAudienceConditions: '',
    trainingAudienceConditionStatus: '',
    observerTrainerConditions: '',
    observerTrainerConditionStatus: '',
    battleRhythmConditions: '',
    battleRhythmConditionStatus: '',
    cisConditions: '',
    cisConditionStatus: '',
    simulationConditions: '',
    simulationConditionStatus: '',
    description: '',
    subtasks: '',
    log: '',
    comment: '',
    trainingObjectiveStatusId: '',
    attachments: [],
    objectiveTypeId: '',
    objectiveOverwrittenValue: null,
    deducedFromObjectiveId: '',
  };

  const dispatch = useDispatch();
  const fileInputRef = useRef<any>();
  const [form] = Form.useForm();
  const { TextArea } = Input;

  const [modalInEditMode, setModalInEditMode] = useState<boolean>(false);
  const [addOntologyModal, setAddOntologyModal] = useState<boolean>(false);
  const [ontologyDataInForm, setOntologyDataInForm] = useState<OntologyDataType>();
  const [fieldsId, setFieldsId] = useState<any[]>([]);
  const [logsData, setLogsData] = useState<LogsData[]>([]);
  const [addOntologyType, setAddOntologyType] = useState<number | undefined>(undefined);

  const [attachments, setAttachments] = useState<any[]>([]);
  const [deletedStringAttachments, setDeletedStringAttachments] = useState<string[]>([]);
  const [generatedAttachsForFileTable, setGeneratedAttachsForFileTable] = useState<FileTableType[]>([]);
  const { preferredLanguage } = useSelector((state: reduxRootTypes.RootStateType) => state.userState);

  const { ontologies } = useSelector((state: reduxRootTypes.RootStateType) => state.ontologyState);
  const { stateNodeList } = useSelector((state: reduxRootTypes.RootStateType) => state.stateFlowState);
  const userState = useSelector((state: reduxRootTypes.RootStateType) => state.userState);
  const { transactionList } = useSelector((state: reduxRootTypes.RootStateType) => state.transactionState);
  const { fieldsList } = useSelector((state: reduxRootTypes.RootStateType) => state.fieldsState);
  const { trainingObjectiveList } = useSelector((state: reduxRootTypes.RootStateType) => state.trainingObjectiveState);
  const [trainingObjectiveDataInForm, setTrainingObjectiveDataInForm] = useState<TrainingObjectiveDataType>(
    trainingObjectiveForm || defaultTrainingObjectiveData(stateNodeList),
  );
  const handleClickOntology = (type: any) => {
    setAddOntologyType(type);
    setAddOntologyModal(true);
    setModalInEditMode(false);
  };

  const { usersList } = useUsers();

  const onSearch = (value: any) => {};

  // const onFormChangeConverted = (inputLabel: string, value: any) => {
  //   switch (inputLabel) {
  //     case 'relatedStandards':
  //       setTrainingObjectiveDataInForm({
  //         ...trainingObjectiveDataInForm,
  //         relatedStandards: value !== '' ? value : 'AJP-3',
  //       });
  //       break;
  //     default:
  //   }
  // };

  const onFormChange = (inputLabel: keyof TrainingObjectiveDataType, value: any) => {
    setTrainingObjectiveDataInForm({
      ...trainingObjectiveDataInForm,
      [inputLabel]: value,
    });
  };
  let allToStateNames: { id: string | undefined; stateName: string | undefined }[] = [];
  const [allToStateName, setAllToStateName] = useState<{ id: string | undefined; stateName: string | undefined }[]>([]);

  const onItemCheck = (enumType: number, inputLabel?: string) => {
    allToStateNames = [];
    allToStateNames = transactionForSelectList({
      enumType,
      statusId: inputLabel,
      isEditMode,
      userState,
      transactionList,
      stateNodeList,
    });
    setAllToStateName(allToStateNames);
  };

  useEffect(() => {
    if (trainingObjectiveForm && trainingObjectiveForm.attachments && trainingObjectiveForm?.attachments.length !== 0) {
      setAttachments([...trainingObjectiveForm?.attachments]);
    }
  }, [trainingObjectiveForm]);

  useEffect(() => {
    if (
      trainingObjectiveDataInForm &&
      trainingObjectiveDataInForm.attachments !== undefined
      // trainingObjectiveDataInForm?.attachments.length !== 0
    ) {
      setTrainingObjectiveDataInForm({ ...trainingObjectiveDataInForm, attachments });
    }
  }, [attachments]);

  useEffect(() => {
    let incidentForFieldId = [];
    if (fieldsList) {
      incidentForFieldId = fieldsList.filter((obj) => obj.fieldsType === 1 && obj.id === trainingObjectiveForm?.id);
      incidentForFieldId = incidentForFieldId.map((e) => e.id);
      setFieldsId(incidentForFieldId);
    }
  }, [fieldsList]);

  const getLogsData = async () => {
    try {
      const logData = await logsService.lookupLogs({
        fieldIds: fieldsId.length > 0 ? fieldsId : undefined,
        blockSpinner: true,
      });

      const tempLog: LogsData[] = [];
      logData.data.rows.forEach((obj, index) => {
        const detail = obj.description;

        const match = detail.match(/has been\s+(\w+)/i);
        const actionWord = match?.[1]?.toLowerCase();

        const action =
          actionWord === 'created'
            ? 'CREATE'
            : actionWord === 'updated'
            ? 'UPDATE'
            : actionWord === 'deleted'
            ? 'DELETE'
            : 'Unknown';

        const username = usersList.find((e) => e.id === obj.createdById)?.userName || '-';

        tempLog.push({
          key: index,
          user: username,
          action,
          details: detail,
          timestamp: moment(obj.createdAt).format('HH:mm DD/MM/YYYY'),
        });
      });

      setLogsData(tempLog);

      // dispatch(contentsActions.incidentFolderList(incidentFolderData.data.rows));
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (fieldsId.length > 0) {
      getLogsData();
    }
  }, [fieldsId]);

  const onFormSave = async () => {
    if (isEditMode) {
      try {
        const trainingObjectiveUpdateData = await objectiveService.trainingObjectiveEdit({
          ...trainingObjectiveDataInForm,
          modificationDate:
            trainingObjectiveForm?.modificationDate === trainingObjectiveDataInForm.modificationDate
              ? moment(new Date())
              : trainingObjectiveDataInForm.modificationDate,
          deletedAttachments: deletedStringAttachments,
        });

        setTrainingObjectiveDataInForm(defaultTrainingObjectiveDatas);
        dispatch(contentsActions.trainingObjectiveEdited(trainingObjectiveUpdateData.data));
        displaySuccessStatusMessage(t('success.edited'));
        setOnClickOpen(false);
      } catch (error: any) {
        if (error !== errorLocals.errorHandledError) {
          statusHelper.displayErrorMessage(error.response);
        }
      }
    } else {
      try {
        const trainingObjectiveAddData = await objectiveService.createTrainingObjective(trainingObjectiveDataInForm, {
          blockErrorHandler: true,
        });

        setTrainingObjectiveDataInForm(defaultTrainingObjectiveDatas);
        dispatch(contentsActions.trainingObjectiveAdded(trainingObjectiveAddData.data));
        dispatch(contentsActions.fieldListAdd({ id: trainingObjectiveAddData.data.id, fieldsType: 1 }));
        displaySuccessStatusMessage(t('success.added'));
        setOnClickOpen(false);
      } catch (error) {
        if (error !== errorLocals.errorHandledError) {
          // @ts-ignore
          statusHelper.displayErrorMessage(error.response.data.message);
        }
      }
    }
    setModalInEditMode(false);
  };

  useEffect(() => {
    if (isEditMode !== true) {
      form.setFieldsValue({
        ...trainingObjectiveDataInForm,
      });
    }
  }, [trainingObjectiveDataInForm]);

  useEffect(() => {
    const tempArray: any[] = [];
    if (attachments === null) {
      setAttachments([]);
    } else {
      attachments?.forEach((element: any) => {
        if (element.url) {
          tempArray.push({
            name: element.file.name,
            size: element.file.size,
            uploadedAt: element.file.lastModifiedDate,
            path: '',
            mediaType: element.file.type,
          });
        } else {
          tempArray.push(element);
        }
      });
      setGeneratedAttachsForFileTable(tempArray);
    }
  }, [attachments]);

  const handleOnChangeUploadInput = async (
    onChangeInputEvent: React.ChangeEvent<HTMLInputElement>,
    index: number | undefined = undefined,
  ) => {
    const getUploadInput = await attachmentHelper.handleOnChangeUploadInput(
      onChangeInputEvent,
      index,
      attachments,
      preferredLanguage,
      t,
    );

    if (getUploadInput?.newImages) {
      setAttachments(getUploadInput.newImages);
    }
    // setReplacedImageIndexes([...replacedImageIndexes, ...getUploadInput?.currentReplacedIndexes]);
  };

  const deleteAttachment = (attachment: any) => {
    if (attachment.url) {
      setAttachments(attachments.filter((atth: any) => atth !== attachment));
    } else {
      setAttachments(attachments.filter((atth: any) => atth !== attachment));
      setDeletedStringAttachments([...deletedStringAttachments, attachment.path]);
    }
  };

  return (
    <>
      {onClickOpen && (
        <DraggableModal
          headerText={isEditMode ? t('OMM.objective.editObjective') : t('OMM.objective.addObjective')}
          onClickClose={() => setOnClickOpen(false)}
          style={{ width: '800px', height: '600px' }}
        >
          <Form
            initialValues={trainingObjectiveDataInForm}
            scrollToFirstError={{ behavior: 'smooth' }}
            onFinish={onFormSave}
            form={form}
          >
            <div className="popup-main-container">
              <div className="popup-container">
                <div className="popup-content" style={{ padding: 0 }}>
                  <div className={styles.container} style={{ padding: 10, marginRight: 15 }}>
                    <div className="popup-row grid-col-2-fixed">
                      <div className="popup-input-container">
                        <span className="popup-input-title">{t('general.event')}</span>
                        <Input className={styles.standard_input} value={eventName} disabled />
                      </div>
                      <div className="popup-input-container">
                        <span className="popup-input-title">{t('OMM.objective.trainingObjectiveNumber')}</span>
                        <Form.Item
                          name="trainingObjectiveNumber"
                          rules={[{ required: true, message: t('general.required') || '' }]}
                        >
                          <Input
                            className={styles.standard_input}
                            value={trainingObjectiveDataInForm.trainingObjectiveNumber}
                            onChange={(e) => onFormChange('trainingObjectiveNumber', e.target.value)}
                          />
                        </Form.Item>
                      </div>
                    </div>
                    <div className="popup-input-container">
                      <span className="popup-input-title">{t('OMM.objective.trainingObjective')}</span>
                      <Form.Item
                        name="trainingObjective"
                        rules={[{ required: true, message: t('general.required') || '' }]}
                      >
                        <Input
                          className={styles.standard_input}
                          value={trainingObjectiveDataInForm.trainingObjective}
                          onChange={(e) => onFormChange('trainingObjective', e.target.value)}
                        />
                      </Form.Item>
                    </div>
                    <div className="popup-row grid-col-2-fixed">
                      <div className="popup-row">
                        <div className="popup-input-container">
                          <span className="popup-input-title">{t('OMM.objective.objectiveTypeId')}</span>
                          <Form.Item
                            name="objectiveTypeId"
                            rules={[{ required: true, message: t('general.required') || '' }]}
                          >
                            <Select
                              showSearch
                              className={styles.select}
                              showArrow
                              optionFilterProp="children"
                              // placeholder="Select an audience process"
                              style={{ width: '645px' }}
                              maxTagCount="responsive"
                              value={trainingObjectiveDataInForm.objectiveTypeId}
                              onChange={(e) => onFormChange('objectiveTypeId', e as string)}
                            >
                              {ontologies
                                .filter((obj) => Number(obj.ontologyType) === OntologyEnum.Objective_Type)
                                .map((option) => (
                                  <Select.Option value={option.id}>
                                    {option.abbreviation && predefinedOntologies.includes(option.abbreviation)
                                      ? t(`administration.ontologies.${option.abbreviation}`)
                                      : option.abbreviation}
                                  </Select.Option>
                                ))}
                            </Select>
                          </Form.Item>
                        </div>
                      </div>
                      <div className="popup-input-container">
                        <Button
                          className={styles.add_button}
                          disabled={checkPermissionList(PermissionEnum.OntologiesCreate)}
                          onClick={() => handleClickOntology(OntologyEnum.Objective_Type)}
                        >
                          {t('general.add')}
                        </Button>
                      </div>
                    </div>
                    <div className="popup-input-container">
                      <span className="popup-input-title">{t('OMM.objective.deducedFromObjectiveId')}</span>
                      <Form.Item name="deducedFromObjectiveId">
                        <Select
                          showSearch
                          className={styles.select}
                          showArrow
                          optionFilterProp="children"
                          maxTagCount="responsive"
                          value={trainingObjectiveDataInForm.deducedFromObjectiveId}
                          onChange={(e) => onFormChange('deducedFromObjectiveId', e)}
                        >
                          {trainingObjectiveList.map((option) => {
                            const foundOnt = ontologies.find((ont) => ont.id === option.objectiveTypeId)?.abbreviation;
                            return (
                              <Select.Option value={option.id}>{`${option.trainingObjective} ${
                                foundOnt ? '/ ' + foundOnt : ''
                              }`}</Select.Option>
                            );
                          })}
                        </Select>
                      </Form.Item>
                    </div>
                    <div className="popup-row grid-col-2-fixed">
                      <div className="popup-input-container">
                        <span className="popup-input-title">{t('general.creationDate')}</span>
                        <div>
                          <Form.Item name="creationDate">
                            <DatePicker
                              showTime
                              format="YYYY-MM-DD HH:mm:ss"
                              style={{ width: '100%', marginTop: '5px' }}
                              value={moment(trainingObjectiveDataInForm.creationDate)}
                              defaultPickerValue={moment(trainingObjectiveDataInForm.creationDate)}
                              disabled
                            />
                          </Form.Item>
                        </div>
                      </div>
                      <div className="popup-input-container">
                        <span className="popup-input-title">{t('general.modificationDate')}</span>
                        <div>
                          <Form.Item
                            name="modificationDate"
                            rules={[{ required: false, message: t('general.required') || '' }]}
                          >
                            <DatePicker
                              showTime
                              format="YYYY-MM-DD HH:mm:ss"
                              style={{ width: '100%', marginTop: '5px' }}
                              value={isEditMode ? moment(trainingObjectiveDataInForm.modificationDate) : undefined}
                              // defaultPickerValue={moment(
                              //   moment().format('YYYY-MM-DD HH:mm:ss').concat(' - 23:59'),
                              //   'YYYY-MM-DD HH:mm:ss',
                              // )}
                              onChange={(e) => onFormChange('modificationDate', e)}
                              disabled
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </div>
                    <div className="popup-row grid-col-2-fixed">
                      <div className="popup-row">
                        <div className="popup-input-container">
                          <span className="popup-input-title">{t('general.relatedAudienceEntities')}</span>
                          <Form.Item
                            name="relatedAudienceEntities"
                            rules={[{ required: true, message: t('general.required') || '' }]}
                          >
                            <Select
                              placement="bottomLeft"
                              showSearch
                              className={styles.select}
                              mode="multiple"
                              showArrow
                              // placeholder="Select an audience entity"
                              optionFilterProp="children"
                              style={{ width: '645px' }}
                              defaultValue={trainingObjectiveDataInForm.relatedAudienceEntities}
                              onChange={(e) => onFormChange('relatedAudienceEntities', e as string[])}
                            >
                              {ontologies
                                .filter((obj) => Number(obj.ontologyType) === OntologyEnum.Player_Audience_Entity)
                                .map((option) => (
                                  <Select.Option value={option.id}>{option.abbreviation}</Select.Option>
                                ))}
                            </Select>
                          </Form.Item>
                        </div>
                      </div>
                      <div className="popup-input-container">
                        <Button
                          className={styles.add_button}
                          disabled={checkPermissionList(PermissionEnum.OntologiesCreate)}
                          onClick={() => handleClickOntology(OntologyEnum.Player_Audience_Entity)}
                        >
                          {t('general.add')}
                        </Button>
                      </div>
                    </div>
                    <div className="popup-row grid-col-2-fixed">
                      <div className="popup-input-container">
                        <span className="popup-input-title">{t('general.relatedAudienceProcesses')}</span>
                        <Form.Item
                          name="relatedAudienceProcesses"
                          rules={[{ required: true, message: t('general.required') || '' }]}
                        >
                          <Select
                            showSearch
                            className={styles.select}
                            mode="multiple"
                            showArrow
                            // placeholder="Select an audience process"
                            optionFilterProp="children"
                            style={{ width: '645px' }}
                            maxTagCount="responsive"
                            defaultValue={trainingObjectiveDataInForm.relatedAudienceProcesses}
                            onChange={(e) => onFormChange('relatedAudienceProcesses', e as string[])}
                          >
                            {ontologies
                              .filter((obj) => Number(obj.ontologyType) === OntologyEnum.Training_Audience_Process)
                              .map((option) => (
                                <Select.Option value={option.id}>{option.abbreviation}</Select.Option>
                              ))}
                          </Select>
                        </Form.Item>
                      </div>
                      <div className="popup-input-container">
                        <Button
                          className={styles.add_button}
                          disabled={checkPermissionList(PermissionEnum.OntologiesCreate)}
                          onClick={() => handleClickOntology(OntologyEnum.Training_Audience_Process)}
                        >
                          {t('general.add')}
                        </Button>
                      </div>
                    </div>

                    <div className="popup-input-container">
                      <span className="popup-input-title">{t('OMM.objective.relatedStandards')}</span>
                      <Form.Item
                        name="relatedStandards"
                        rules={[{ required: true, message: t('general.required') || '' }]}
                      >
                        <Input
                          className={styles.standard_input}
                          value={isEditMode ? trainingObjectiveDataInForm.relatedStandards : undefined}
                          onChange={(e) => onFormChange('relatedStandards', e.target.value)}
                        />
                      </Form.Item>
                    </div>
                    <div className="popup-row grid-col-2-fixed">
                      <div className="popup-row">
                        <div className="popup-input-container-private">
                          {/* <span className="popup-input-title">EXERCISE CONTROL CONDITIONS</span>
                          <Form.Item
                            name="exerciseControlConditions"
                            rules={[{ required: false, message: t('general.required') || '' }]}
                          >
                            <Input
                              className={styles.standard_input}
                              value={
                                isEditMode ? trainingObjectiveDataInForm.exerciseControlConditions : 'To be analyzed'
                              }
                              onChange={(e) => onFormChange('exerciseControlConditions', e.target.value)}
                            />
                          </Form.Item> */}
                          <TextAreaWithPopover
                            content={trainingObjectiveDataInForm.exerciseControlConditions}
                            title={t('OMM.objective.exerciseControlConditions')}
                            name={'exerciseControlConditions'}
                            value={
                              isEditMode ? trainingObjectiveDataInForm.exerciseControlConditions : 'To be analyzed'
                            }
                            onFormChange={onFormChange}
                          />
                        </div>
                      </div>

                      <div className="popup-row">
                        <div className="popup-input-container-private">
                          <span className="popup-input-title">{t('general.status')}</span>
                          <div>
                            {/* <Form.Item
                              name="exerciseControlConditionStatus"
                              rules={[{ required: false, message: t('general.required') || '' }]}
                            > */}
                            <Select
                              className={styles.select}
                              showSearch
                              placeholder="Select a exercise conditions status"
                              optionFilterProp="children"
                              onSearch={onSearch}
                              style={{ width: '100%' }}
                              value={
                                stateNodeList.find(
                                  (e) => e.id === trainingObjectiveDataInForm.exerciseControlConditionStatus,
                                )?.stateName
                              }
                              onChange={(e) => onFormChange('exerciseControlConditionStatus', e as string)}
                              onClick={() =>
                                onItemCheck(
                                  flowOMMEnums.EXERCISE_CONTROL_CONDITION,
                                  trainingObjectiveDataInForm.exerciseControlConditionStatus,
                                )
                              }
                            >
                              {isEditMode && allToStateName.length > 0
                                ? Object.values(allToStateName).map((option) => (
                                    <Select.Option key={option.id} value={option.id}>
                                      {option.stateName}
                                    </Select.Option>
                                  ))
                                : Object.values(allToStateName).map((option) => (
                                    <Select.Option key={option.id} value={option.id}>
                                      {option.stateName}
                                    </Select.Option>
                                  ))}
                            </Select>
                            {/* </Form.Item> */}
                          </div>
                        </div>
                      </div>

                      <div className="popup-row">
                        <div className="popup-input-container-private">
                          {/* <span className="popup-input-title">TRAINING AUDIENCE CONDITIONS</span>
                          <Form.Item
                            name="trainingAudienceConditions"
                            rules={[{ required: false, message: t('general.required') || '' }]}
                          >
                            <Input
                              className={styles.standard_input}
                              value={
                                isEditMode ? trainingObjectiveDataInForm.trainingAudienceConditions : 'To be analyzed'
                              }
                              onChange={(e) => onFormChange('trainingAudienceConditions', e.target.value)}
                            />
                          </Form.Item> */}
                          <TextAreaWithPopover
                            content={trainingObjectiveDataInForm.trainingAudienceConditions}
                            title={t('OMM.objective.trainingAudienceConditions')}
                            name={'trainingAudienceConditions'}
                            value={
                              isEditMode ? trainingObjectiveDataInForm.trainingAudienceConditions : 'To be analyzed'
                            }
                            onFormChange={onFormChange}
                          />
                        </div>
                      </div>

                      <div className="popup-row" style={{ marginTop: i18n.language === 'en' ? 19 : 0 }}>
                        <div className="popup-input-container-private">
                          <span className="popup-input-title">{t('general.status')}</span>
                          <div>
                            <Select
                              className={styles.select}
                              showSearch
                              placeholder="Select a training conditions status"
                              optionFilterProp="children"
                              onSearch={onSearch}
                              style={{ width: '100%' }}
                              value={
                                stateNodeList.find(
                                  (e) => e.id === trainingObjectiveDataInForm.trainingAudienceConditionStatus,
                                )?.stateName
                              }
                              onChange={(e) => onFormChange('trainingAudienceConditionStatus', e as string)}
                              onClick={() =>
                                onItemCheck(
                                  flowOMMEnums.TRAINING_AUDIENCE_CONDITION,
                                  trainingObjectiveDataInForm.trainingAudienceConditionStatus,
                                )
                              }
                            >
                              {isEditMode && allToStateName.length > 0
                                ? Object.values(allToStateName).map((option) => (
                                    <Select.Option key={option.id} value={option.id}>
                                      {option.stateName}
                                    </Select.Option>
                                  ))
                                : Object.values(allToStateName).map((option) => (
                                    <Select.Option key={option.id} value={option.id}>
                                      {option.stateName}
                                    </Select.Option>
                                  ))}
                            </Select>
                          </div>
                        </div>
                      </div>

                      <div className="popup-row">
                        <div className="popup-input-container-private">
                          <TextAreaWithPopover
                            content={trainingObjectiveDataInForm.observerTrainerConditions}
                            title={t('OMM.objective.observerTrainerConditions')}
                            name={'observerTrainerConditions'}
                            value={
                              isEditMode ? trainingObjectiveDataInForm.observerTrainerConditions : 'To be analyzed'
                            }
                            onFormChange={onFormChange}
                          />
                        </div>
                      </div>

                      <div className="popup-row">
                        <div className="popup-input-container-private">
                          <span className="popup-input-title">{t('general.status')}</span>
                          <div>
                            {/* <Form.Item
                              name="observerTrainerConditionStatus"
                              rules={[{ required: true, message: t('general.required') || '' }]}
                            > */}
                            <Select
                              className={styles.select}
                              showSearch
                              optionFilterProp="children"
                              onSearch={onSearch}
                              style={{ width: '100%' }}
                              value={
                                stateNodeList.find(
                                  (e) => e.id === trainingObjectiveDataInForm.observerTrainerConditionStatus,
                                )?.stateName
                              }
                              onChange={(e) => onFormChange('observerTrainerConditionStatus', e as string)}
                              onClick={() =>
                                onItemCheck(
                                  flowOMMEnums.OBSERVER_TRAINER_CONDITION,
                                  trainingObjectiveDataInForm.observerTrainerConditionStatus,
                                )
                              }
                            >
                              {isEditMode && allToStateName.length > 0
                                ? Object.values(allToStateName).map((option) => (
                                    <Select.Option key={option.id} value={option.id}>
                                      {option.stateName}
                                    </Select.Option>
                                  ))
                                : Object.values(allToStateName).map((option) => (
                                    <Select.Option key={option.id} value={option.id}>
                                      {option.stateName}
                                    </Select.Option>
                                  ))}
                            </Select>
                            {/* </Form.Item> */}
                          </div>
                        </div>
                      </div>

                      <div className="popup-row">
                        {/* <Popover
                          content={trainingObjectiveDataInForm.scenarioConditions}
                          visible={inputHovered && !inputFocused}
                        >
                          <div className="popup-input-container-private">
                            <span className="popup-input-title">SCENARIO CONDITIONS</span>
                            <Form.Item name="scenarioConditions" rules={[{ required: false, message: t('general.required') || '' }]}>
                              <TextArea
                                onFocus={() => setInputFocused(true)}
                                onBlur={() => setInputFocused(false)}
                                onMouseEnter={() => setInputHovered(true)}
                                onMouseLeave={() => setInputHovered(false)}
                                className={styles.standard_input}
                                value={isEditMode ? trainingObjectiveDataInForm.scenarioConditions : 'To be analyzed'}
                                onChange={(e) => onFormChange('scenarioConditions', e.target.value)}
                              />
                            </Form.Item>
                          </div>
                        </Popover> */}
                        <TextAreaWithPopover
                          content={trainingObjectiveDataInForm.scenarioConditions}
                          title={t('general.scenarioConditions')}
                          name="scenarioConditions"
                          value={
                            isEditMode
                              ? trainingObjectiveDataInForm.scenarioConditions
                              : "t('OMM.objective.toBeAnalyzed')"
                          }
                          onFormChange={onFormChange}
                        />
                      </div>
                      <div className="popup-row">
                        <div className="popup-input-container-private">
                          <span className="popup-input-title">{t('general.status')}</span>
                          <div>
                            {/* <Form.Item
                              name="scenarioConditionStatus"
                              rules={[{ required: false, message: t('general.required') || '' }]}
                            > */}
                            <Select
                              className={styles.select}
                              showSearch
                              placeholder="Select a scenario conditions status"
                              optionFilterProp="children"
                              onSearch={onSearch}
                              style={{ width: '100%' }}
                              value={
                                stateNodeList.find((e) => e.id === trainingObjectiveDataInForm.scenarioConditionStatus)
                                  ?.stateName
                              }
                              onChange={(e) => onFormChange('scenarioConditionStatus', e as string)}
                              onClick={() =>
                                onItemCheck(
                                  flowOMMEnums.SCENARIO_CONDITION,
                                  trainingObjectiveDataInForm.scenarioConditionStatus,
                                )
                              }
                            >
                              {isEditMode && allToStateName.length > 0
                                ? Object.values(allToStateName).map((option) => (
                                    <Select.Option key={option.id} value={option.id}>
                                      {option.stateName}
                                    </Select.Option>
                                  ))
                                : Object.values(allToStateName).map((option) => (
                                    <Select.Option key={option.id} value={option.id}>
                                      {option.stateName}
                                    </Select.Option>
                                  ))}
                            </Select>
                            {/* </Form.Item> */}
                          </div>
                        </div>
                      </div>
                      <div className="popup-row">
                        <div className="popup-input-container-private">
                          {/* <span className="popup-input-title">MIL CONDITIONS</span>
                          <Form.Item name="milConditions" rules={[{ required: false, message: t('general.required') || '' }]}>
                            <Input
                              className={styles.standard_input}
                              value={isEditMode ? trainingObjectiveDataInForm.milConditions : 'To be analyzed'}
                              onChange={(e) => onFormChange('milConditions', e.target.value)}
                            />
                          </Form.Item> */}
                          <TextAreaWithPopover
                            content={trainingObjectiveDataInForm.milConditions}
                            title={t('general.milConditions')}
                            name="milConditions"
                            value={isEditMode ? trainingObjectiveDataInForm.milConditions : 'To be analyzed'}
                            onFormChange={onFormChange}
                          />
                        </div>
                      </div>
                      <div className="popup-row">
                        <div className="popup-input-container-private">
                          <span className="popup-input-title">{t('general.status')}</span>
                          <div>
                            {/* <Form.Item name="milConditionStatus" rules={[{ required: false, message: t('general.required') || '' }]}> */}
                            <Select
                              className={styles.select}
                              showSearch
                              placeholder="Select a mil conditions status"
                              optionFilterProp="children"
                              onSearch={onSearch}
                              style={{ width: '100%' }}
                              value={
                                stateNodeList.find((e) => e.id === trainingObjectiveDataInForm.milConditionStatus)
                                  ?.stateName
                              }
                              onChange={(e) => onFormChange('milConditionStatus', e as string)}
                              onClick={() =>
                                onItemCheck(flowOMMEnums.MIL_CONDITION, trainingObjectiveDataInForm.milConditionStatus)
                              }
                            >
                              {isEditMode && allToStateName.length > 0
                                ? Object.values(allToStateName).map((option) => (
                                    <Select.Option key={option.id} value={option.id}>
                                      {option.stateName}
                                    </Select.Option>
                                  ))
                                : Object.values(allToStateName).map((option) => (
                                    <Select.Option key={option.id} value={option.id}>
                                      {option.stateName}
                                    </Select.Option>
                                  ))}
                            </Select>
                            {/* </Form.Item> */}
                          </div>
                        </div>
                      </div>

                      <div className="popup-row">
                        <div className="popup-input-container-private">
                          {/* <span className="popup-input-title">CIS CONDITIONS</span>
                          <Form.Item name="cisConditions" rules={[{ required: false, message: t('general.required') || '' }]}>
                            <Input
                              className={styles.standard_input}
                              value={isEditMode ? trainingObjectiveDataInForm.cisConditions : 'To be analyzed'}
                              onChange={(e) => onFormChange('cisConditions', e.target.value)}
                            />
                          </Form.Item> */}
                          <TextAreaWithPopover
                            content={trainingObjectiveDataInForm.cisConditions}
                            title={t('OMM.objective.cisConditions')}
                            name={'cisConditions'}
                            value={isEditMode ? trainingObjectiveDataInForm.cisConditions : 'To be analyzed'}
                            onFormChange={onFormChange}
                          />
                        </div>
                      </div>
                      <div className="popup-row">
                        <div className="popup-input-container-private">
                          <span className="popup-input-title">{t('general.status')}</span>
                          <div>
                            {/* <Form.Item name="cisConditionStatus" rules={[{ required: false, message: t('general.required') || '' }]}> */}
                            <Select
                              className={styles.select}
                              showSearch
                              placeholder="Select a cis conditions status"
                              optionFilterProp="children"
                              onSearch={onSearch}
                              style={{ width: '100%' }}
                              value={
                                stateNodeList.find((e) => e.id === trainingObjectiveDataInForm.cisConditionStatus)
                                  ?.stateName
                              }
                              onChange={(e) => onFormChange('cisConditionStatus', e as string)}
                              onClick={() =>
                                onItemCheck(flowOMMEnums.CIS_CONDITION, trainingObjectiveDataInForm.cisConditionStatus)
                              }
                            >
                              {isEditMode && allToStateName.length > 0
                                ? Object.values(allToStateName).map((option) => (
                                    <Select.Option key={option.id} value={option.id}>
                                      {option.stateName}
                                    </Select.Option>
                                  ))
                                : Object.values(allToStateName).map((option) => (
                                    <Select.Option key={option.id} value={option.id}>
                                      {option.stateName}
                                    </Select.Option>
                                  ))}
                            </Select>
                            {/* </Form.Item> */}
                          </div>
                        </div>
                      </div>
                      <div className="popup-row">
                        <div className="popup-input-container-private">
                          {/* <span className="popup-input-title">SIMULATION CONDITIONS</span>
                          <Form.Item name="simulationConditions" rules={[{ required: false, message: t('general.required') || '' }]}>
                            <Input
                              className={styles.standard_input}
                              value={isEditMode ? trainingObjectiveDataInForm.simulationConditions : 'To be analyzed'}
                              onChange={(e) => onFormChange('simulationConditions', e.target.value)}
                            />
                          </Form.Item> */}
                          <TextAreaWithPopover
                            content={trainingObjectiveDataInForm.simulationConditions}
                            title={t('OMM.objective.simulationConditions')}
                            name={'simulationConditions'}
                            value={isEditMode ? trainingObjectiveDataInForm.simulationConditions : 'To be analyzed'}
                            onFormChange={onFormChange}
                          />
                        </div>
                      </div>
                      <div className="popup-row">
                        <div className="popup-input-container-private">
                          <span className="popup-input-title">{t('general.status')}</span>
                          <div>
                            {/* <Form.Item
                              name="simulationConditionStatus"
                              rules={[{ required: false, message: t('general.required') || '' }]}
                            > */}
                            <Select
                              className={styles.select}
                              showSearch
                              placeholder="Select a simulation conditions status"
                              optionFilterProp="children"
                              onSearch={onSearch}
                              style={{ width: '100%' }}
                              value={
                                stateNodeList.find(
                                  (e) => e.id === trainingObjectiveDataInForm.simulationConditionStatus,
                                )?.stateName
                              }
                              onChange={(e) => onFormChange('simulationConditionStatus', e as string)}
                              onClick={() =>
                                onItemCheck(
                                  flowOMMEnums.SIMULATION_CONDITION,
                                  trainingObjectiveDataInForm.simulationConditionStatus,
                                )
                              }
                            >
                              {isEditMode && allToStateName.length > 0
                                ? Object.values(allToStateName).map((option) => (
                                    <Select.Option key={option.id} value={option.id}>
                                      {option.stateName}
                                    </Select.Option>
                                  ))
                                : Object.values(allToStateName).map((option) => (
                                    <Select.Option key={option.id} value={option.id}>
                                      {option.stateName}
                                    </Select.Option>
                                  ))}
                            </Select>
                            {/* </Form.Item> */}
                          </div>
                        </div>
                      </div>

                      <div className="popup-row">
                        <div className="popup-input-container-private">
                          <TextAreaWithPopover
                            content={trainingObjectiveDataInForm.battleRhythmConditions}
                            title={t('OMM.objective.battleRhythmConditions')}
                            name={'battleRhythmConditions'}
                            value={isEditMode ? trainingObjectiveDataInForm.battleRhythmConditions : 'To be analyzed'}
                            onFormChange={onFormChange}
                          />
                        </div>
                      </div>

                      <div className="popup-row">
                        <div className="popup-input-container-private">
                          <span className="popup-input-title">{t('general.status')}</span>
                          <div>
                            {/* <Form.Item
                              name="observerTrainerConditionStatus"
                              rules={[{ required: true, message: t('general.required') || '' }]}
                            > */}
                            <Select
                              className={styles.select}
                              showSearch
                              optionFilterProp="children"
                              onSearch={onSearch}
                              style={{ width: '100%' }}
                              value={
                                stateNodeList.find(
                                  (e) => e.id === trainingObjectiveDataInForm.battleRhythmConditionStatus,
                                )?.stateName
                              }
                              onChange={(e) => onFormChange('battleRhythmConditionStatus', e as string)}
                              onClick={() =>
                                onItemCheck(
                                  flowOMMEnums.BATTLE_RHYTHM_CONDITION,
                                  trainingObjectiveDataInForm.battleRhythmConditionStatus,
                                )
                              }
                            >
                              {isEditMode && allToStateName.length > 0
                                ? Object.values(allToStateName).map((option) => (
                                    <Select.Option key={option.id} value={option.id}>
                                      {option.stateName}
                                    </Select.Option>
                                  ))
                                : Object.values(allToStateName).map((option) => (
                                    <Select.Option key={option.id} value={option.id}>
                                      {option.stateName}
                                    </Select.Option>
                                  ))}
                            </Select>
                            {/* </Form.Item> */}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="popup-input-container">
                      <span className="popup-input-title">{t('general.descriptionTask')}</span>
                      <div>
                        <Form.Item
                          name="description"
                          rules={[{ required: false, message: t('general.required') || '' }]}
                        >
                          <TextArea
                            autoSize={{ minRows: 3, maxRows: 10 }}
                            showCount
                            maxLength={2048}
                            value={trainingObjectiveDataInForm.description}
                            onChange={(e) => onFormChange('description', e.target.value)}
                          />
                        </Form.Item>
                      </div>
                    </div>
                    <div className="popup-input-container">
                      <span className="popup-input-title">{t('OMM.objective.subtasks')}</span>
                      <div>
                        <Form.Item name="subtasks" rules={[{ required: false, message: t('general.required') || '' }]}>
                          <TextArea
                            autoSize={{ minRows: 3, maxRows: 10 }}
                            showCount
                            maxLength={2048}
                            value={trainingObjectiveDataInForm.subtasks}
                            onChange={(e) => onFormChange('subtasks', e.target.value)}
                          />
                        </Form.Item>
                      </div>
                    </div>
                    <div className="popup-input-container">
                      <span className="popup-input-title">{t('general.log')}</span>

                      <Form.Item name="log">
                        <Logs logsData={logsData} translationPath="OMM.objective" location={t('OMM.tabs.objective')} />
                      </Form.Item>
                    </div>
                    <div className="popup-input-container">
                      <span className="popup-input-title">{t('general.comment')}</span>
                      <div>
                        <Form.Item name="comment" rules={[{ required: false, message: t('general.required') || '' }]}>
                          <TextArea
                            rows={3}
                            showCount
                            maxLength={256}
                            value={trainingObjectiveDataInForm.comment}
                            onChange={(e) => onFormChange('comment', e.target.value)}
                          />
                        </Form.Item>
                      </div>
                    </div>
                    <div className="popup-input-container">
                      <span className="popup-input-title">{t('OMM.objective.trainingObjectiveStatusId')}</span>
                      <div>
                        {/* <Form.Item name="trainingObjectiveStatusId" rules={[{ required: false, message: t('general.required') || '' }]}> */}
                        <Select
                          className={styles.select}
                          showSearch
                          placeholder="Select an objective status"
                          optionFilterProp="children"
                          onSearch={onSearch}
                          style={{ width: '100%' }}
                          // defaultValue={trainingObjectiveDataInForm.trainingObjectiveStatusId || undefined}
                          value={
                            stateNodeList.find((e) => e.id === trainingObjectiveDataInForm.trainingObjectiveStatusId)
                              ?.stateName
                          }
                          onChange={(e) => onFormChange('trainingObjectiveStatusId', e as string)}
                          onClick={() =>
                            onItemCheck(flowOMMEnums.OBJECTIVE, trainingObjectiveDataInForm.trainingObjectiveStatusId)
                          }
                        >
                          {isEditMode && allToStateName.length > 0
                            ? Object.values(allToStateName).map((option) => (
                                <Select.Option key={option.id} value={option.id}>
                                  {option.stateName}
                                </Select.Option>
                              ))
                            : Object.values(allToStateName).map((option) => (
                                <Select.Option key={option.id} value={option.id}>
                                  {option.stateName}
                                </Select.Option>
                              ))}
                        </Select>
                        {/* </Form.Item> */}
                      </div>
                    </div>
                    <div className={styles.attachmentsTitle}>
                      <Form.Item name="attachment" rules={[{ required: false, message: t('general.required') || '' }]}>
                        <Button
                          className={'attachment'}
                          onClick={() => fileInputRef.current.click()}
                          disabled={attachments?.length > 2}
                          type="primary"
                          icon={<UploadOutlined style={{ marginRight: '5px' }} />}
                        >
                          <input
                            type="file"
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={(e) => handleOnChangeUploadInput(e)}
                          />
                          {t('general.addAttachment')}
                        </Button>
                      </Form.Item>
                    </div>
                    <FileTable
                      dataSrc={generatedAttachsForFileTable}
                      deleteRow={deleteAttachment}
                      attachments={attachments}
                    />
                  </div>
                </div>
              </div>
              <div className="popup-footer" style={{ marginRight: '6%', marginTop: '2%' }}>
                <div>
                  <button className="next-button" type="submit">
                    {t('general.save')}
                  </button>
                  <button className="modal-button" onClick={() => setOnClickOpen(false)} type="button">
                    {t('general.cancel')}
                  </button>
                </div>
              </div>
            </div>
          </Form>
        </DraggableModal>
      )}

      {addOntologyModal && (
        <OntologyForm
          setEditModal={modalInEditMode}
          ontologiesForm={ontologyDataInForm}
          addOntologyType={addOntologyType}
          onClickOpen
          setOnClickOpen={setAddOntologyModal}
        />
      )}
    </>
  );
};

export default TrainingObjectiveForm;
